import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag, AlertTriangle, Truck, Package, UserPlus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const SIGNUP_HINT_DISMISSED_KEY = "fff_drawer_signup_dismissed";

export const CartDrawer = () => {
  const {
    drawerOpen,
    closeDrawer,
    items,
    menu,
    priceMap,
    unitPriceFor,
    subtotal,
    deliveryFee,
    total,
    fulfillment,
    setFulfillment,
    updateQty,
    removeItem,
    minimumMet,
    deliveryMinimum,
  } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // One-time dismissible sign-up nudge for anonymous shoppers — surface in the
  // drawer so they're invited to register *before* they checkout. After they
  // close it the prompt stays hidden across sessions.
  const [signupDismissed, setSignupDismissed] = useState(() => {
    try {
      return localStorage.getItem(SIGNUP_HINT_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const showSignupHint =
    !user && items.length > 0 && !signupDismissed; // user === false (anon) or null (initial)
  const dismissSignup = () => {
    try {
      localStorage.setItem(SIGNUP_HINT_DISMISSED_KEY, "1");
    } catch (e) { /* ignore */ }
    setSignupDismissed(true);
  };

  // Close on Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => e.key === "Escape" && closeDrawer();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, closeDrawer]);

  // Auto-close drawer whenever the route changes so its backdrop never lingers
  // and intercepts clicks on the next page (esp. /cart, /checkout/*).
  useEffect(() => {
    closeDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const nameFor = (id) => menu.items.find((m) => m.id === id)?.name || id;
  const imageFor = (id) => menu.items.find((m) => m.id === id)?.image || null;

  const goCart = () => {
    closeDrawer();
    navigate("/cart");
  };

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="cart-drawer-backdrop"
        onClick={closeDrawer}
        className={`fixed inset-0 z-[60] bg-black/45 backdrop-blur-sm transition-opacity duration-300 ${
          drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      />
      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        aria-hidden={!drawerOpen}
        className={`fixed top-0 right-0 z-[70] h-full w-full sm:w-[420px] bg-white transition-transform duration-300 flex flex-col print:hidden ${
          drawerOpen ? "translate-x-0 shadow-[0_10px_50px_-10px_rgba(0,0,0,0.4)]" : "translate-x-full"
        }`}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-black/5">
          <div className="flex items-center gap-2.5">
            <ShoppingBag className="h-5 w-5 text-brand-charcoal" />
            <h2 className="font-display font-black text-[18px] text-brand-charcoal tracking-tight">
              Your Cart
            </h2>
          </div>
          <button
            type="button"
            data-testid="cart-drawer-close"
            onClick={closeDrawer}
            aria-label="Close cart"
            className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-black/10 hover:bg-brand-cream"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Fulfillment toggle */}
        <div className="px-5 pt-4">
          <div className="grid grid-cols-2 gap-2 rounded-xl bg-brand-cream/60 p-1">
            <button
              type="button"
              data-testid="cart-fulfillment-delivery"
              onClick={() => setFulfillment("delivery")}
              className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                fulfillment === "delivery"
                  ? "bg-brand-charcoal text-white"
                  : "text-brand-charcoal/70 hover:text-brand-charcoal"
              }`}
            >
              <Truck className="h-3.5 w-3.5" /> Delivery
            </button>
            <button
              type="button"
              data-testid="cart-fulfillment-pickup"
              onClick={() => setFulfillment("pickup")}
              className={`flex items-center justify-center gap-1.5 rounded-lg py-2 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                fulfillment === "pickup"
                  ? "bg-brand-charcoal text-white"
                  : "text-brand-charcoal/70 hover:text-brand-charcoal"
              }`}
            >
              <Package className="h-3.5 w-3.5" /> Pickup
            </button>
          </div>
          {fulfillment === "pickup" && (
            <p className="mt-2 text-[11px] text-brand-charcoal/55 leading-snug">
              Pickup waives the ${deliveryMinimum} minimum and delivery fee.
            </p>
          )}
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="cart-empty">
              <ShoppingBag className="h-10 w-10 text-brand-charcoal/25" />
              <p className="mt-4 font-display font-bold text-[16px] text-brand-charcoal">
                Your cart is empty
              </p>
              <p className="mt-1 text-[13px] text-brand-charcoal/60 max-w-[260px]">
                Browse our packages and start building your event.
              </p>
              <Link
                to="/menu"
                onClick={closeDrawer}
                data-testid="cart-empty-browse-btn"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-4 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
              >
                Browse Menu
              </Link>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="cart-items-list">
              {items.map((it) => {
                const unit = unitPriceFor(it);
                const lineTotal = unit * it.quantity;
                const optsKey = JSON.stringify(it.selected_options || null);
                const lineKey = `${it.package_id}:${optsKey}`;
                const optionLines = [];
                if (it.selected_options) {
                  for (const [grp, vals] of Object.entries(it.selected_options)) {
                    if (Array.isArray(vals) && vals.length > 0) {
                      optionLines.push(`${grp}: ${vals.join(", ")}`);
                    }
                  }
                }
                return (
                  <li
                    key={lineKey}
                    data-testid={`cart-line-${it.package_id}`}
                    className="rounded-2xl border border-black/5 bg-brand-cream/40 p-3.5"
                  >
                    <div className="flex items-start gap-3">
                      {imageFor(it.package_id) ? (
                        <img
                          src={imageFor(it.package_id)}
                          alt={nameFor(it.package_id)}
                          loading="lazy"
                          data-testid={`cart-line-image-${it.package_id}`}
                          className="h-16 w-16 sm:h-[72px] sm:w-[72px] shrink-0 rounded-xl object-cover bg-white border border-black/5"
                        />
                      ) : (
                        <div
                          aria-hidden="true"
                          className="h-16 w-16 sm:h-[72px] sm:w-[72px] shrink-0 rounded-xl bg-brand-cream border border-black/5 inline-flex items-center justify-center"
                        >
                          <ShoppingBag className="h-5 w-5 text-brand-charcoal/30" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-display font-bold text-[14px] text-brand-charcoal tracking-tight leading-tight">
                          {nameFor(it.package_id)}
                        </p>
                        {optionLines.length > 0 && (
                          <p
                            data-testid={`cart-line-options-${it.package_id}`}
                            className="mt-1 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-teal"
                          >
                            {optionLines.join(" · ")}
                          </p>
                        )}
                        <p className="mt-1 text-[12px] text-brand-charcoal/60">
                          ${unit.toFixed(2)} each
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid={`cart-remove-${it.package_id}`}
                        onClick={() => removeItem(it.package_id, optsKey)}
                        aria-label={`Remove ${nameFor(it.package_id)}`}
                        className="text-brand-charcoal/40 hover:text-red-600 transition-colors shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 p-1">
                        <button
                          type="button"
                          data-testid={`cart-dec-${it.package_id}`}
                          aria-label="Decrease quantity"
                          onClick={() => updateQty(it.package_id, it.quantity - 1, optsKey)}
                          className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-brand-cream"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span
                          data-testid={`cart-qty-${it.package_id}`}
                          className="min-w-[20px] text-center font-bold text-[14px] text-brand-charcoal"
                        >
                          {it.quantity}
                        </span>
                        <button
                          type="button"
                          data-testid={`cart-inc-${it.package_id}`}
                          aria-label="Increase quantity"
                          onClick={() => updateQty(it.package_id, it.quantity + 1, optsKey)}
                          className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-brand-cream"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <p className="font-display font-black text-[15px] text-brand-charcoal tabular-nums">
                        ${lineTotal.toFixed(2)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Totals + CTA */}
        {items.length > 0 && (
          <footer className="border-t border-black/5 bg-white px-5 py-4 space-y-3">
            <div className="space-y-1.5 text-[13px]">
              <div className="flex items-center justify-between text-brand-charcoal/75">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal" className="tabular-nums">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-brand-charcoal/75">
                <span>{fulfillment === "delivery" ? "Delivery fee" : "Pickup"}</span>
                <span data-testid="cart-delivery-fee" className="tabular-nums">
                  {fulfillment === "delivery" ? `$${deliveryFee.toFixed(2)}` : "$0.00"}
                </span>
              </div>
              <div className="flex items-center justify-between font-display font-black text-[16px] text-brand-charcoal pt-1">
                <span>Total</span>
                <span data-testid="cart-total" className="tabular-nums">${total.toFixed(2)}</span>
              </div>
            </div>

            {!minimumMet && (
              <div
                data-testid="cart-min-warning"
                className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-[12px] text-amber-900"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Delivery requires a ${deliveryMinimum} minimum. Add ${(deliveryMinimum - subtotal).toFixed(2)} more, or
                  switch to Pickup.
                </span>
              </div>
            )}

            {showSignupHint && (
              <div
                data-testid="cart-drawer-signup-hint"
                className="relative flex items-start gap-2.5 rounded-xl bg-brand-teal/8 border border-brand-teal/25 px-3 py-2.5 pr-7 text-[12px] text-brand-charcoal"
              >
                <UserPlus className="h-4 w-4 mt-0.5 shrink-0 text-brand-teal" />
                <span className="leading-snug">
                  Catering with us often?{" "}
                  <Link
                    to="/register"
                    onClick={closeDrawer}
                    data-testid="cart-drawer-signup-link"
                    className="font-bold text-brand-teal underline-offset-2 hover:underline"
                  >
                    Create an account
                  </Link>{" "}
                  to track orders and re-order in one tap.
                </span>
                <button
                  type="button"
                  onClick={dismissSignup}
                  data-testid="cart-drawer-signup-dismiss"
                  aria-label="Dismiss sign-up prompt"
                  className="absolute top-1.5 right-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-brand-charcoal/40 hover:text-brand-charcoal/80 hover:bg-white/40"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            <button
              type="button"
              data-testid="cart-drawer-checkout-btn"
              onClick={goCart}
              disabled={!minimumMet}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3.5 text-[12.5px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Review & Checkout
            </button>
            <button
              type="button"
              data-testid="cart-drawer-continue-btn"
              onClick={closeDrawer}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-black/10 bg-white text-brand-charcoal px-5 py-3 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-brand-cream transition-colors"
            >
              Continue Shopping
            </button>
          </footer>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
